import { Component, Injector, Input, OnInit } from '@angular/core';
import { WidgetBaseComponent } from '../../widgetBase';
import { take } from 'rxjs/operators';

/**
 * MacroeconomicIndicators Widget
 */
@Component({
  selector: 'macroeconomic-indicators-widget',
  templateUrl: './macroeconomic-indicators-widget.component.html',
  styleUrls: ['../../common/css/general.scss','./macroeconomic-indicators-widget.component.scss']
})
export class MacroeconomicIndicatorsWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Current widget's layout (obsolete?)
   */
  currentLayout = 'manuell';

  /**
   * Current application mode (Edit/View) (obsolete?)
   */
  mode: string;

  /**
   * Current application localization (obsolete?)
   */
  appLocalization = 'de-DE';

  /**
   * Rating options (obsolete?)
   */
  ratingOptions = {
    dataSource: [
      { id: 0, value: '' },
      { id: 1, value: '1' },
      { id: 2, value: '2' },
      { id: 3, value: '3' },
      { id: 4, value: '4' },
      { id: 5, value: '5' }
    ],
    dataTextField: "value",
    dataValueField: "id"
  };

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("macroeconomicIndicators", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('macroeconomicindicators').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('macroeconomicindicators', this.getReport()).pipe();
  }

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'macroeconomicIndicators', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Calculates Population Forecast Rating
   */
  getPopulationForecastRating(): number {
    const difference = this.widgetData.populationForecastDistrict - this.widgetData.populationForecastCountry;

    if (difference <= -7.5) {
      return 1;
    }
    else if (difference <= -2.5) {
      return 2;
    }
    else if (difference <= 0) {
      return 3;
    }
    else if (difference <= 2.5) {
      return 4;
    }
    return 5;
  }

  /**
   * Calculates Unemployment Rate Rating
   */
  getUnemploymentRateRating(): number {
    const difference = this.widgetData.unemploymentRateDistrict - this.widgetData.unemploymentRateCountry;

    if (difference <= -4) {
      return 5;
    }
    else if (difference <= -1.5) {
      return 4;
    }
    else if (difference <= 0) {
      return 3;
    }
    else if (difference <= 2) {
      return 2;
    }
    return 1;
  }

  /**
   * Calculates Purchasing Power Index Rating
   */
  getPurchasingPowerIndexRating(): number {
    const difference = this.widgetData.purchasingPowerIndexDistrict - 100;

    if (difference <= -10) {
      return 1;
    }
    else if (difference <= -3.5) {
      return 2;
    }
    else if (difference <= 0) {
      return 3;
    }
    else if (difference <= 3.5) {
      return 4;
    }
    return 5;
  }

  /**
   * Calculates Disposable Income Per Capita Rating
   */
  getDisposableIncomePerCapitaRating(): number {
    const difference = ((this.widgetData.disposableIncomePerCapitaDistrict / this.widgetData.disposableIncomePerCapitaCountry) - 1) * 100;
    if (difference <= -15) {
      return 1;
    }
    else if (difference <= -3) {
      return 2;
    }
    else if (difference <= 0) {
      return 3;
    }
    else if (difference <= 5) {
      return 4;
    }
    return 5;
  }

  /**
   * Calculates Centrality Index Rating
   */
  getCentralityIndexRating(): number {
    const difference = this.widgetData.centralityIndexDistrict - 100;

    if (difference <= -10) {
      return 1;
    }
    else if (difference <= -3.5) {
      return 2;
    }
    else if (difference <= 0) {
      return 3;
    }
    else if (difference <= 3.5) {
      return 4;
    }
    return 5;
  }
}
